import { AppEvent, logAppEvent } from 'lib/analytics'
import React from 'react'

export interface ErrorBoundaryProps {
  fallback: React.ReactNode
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

interface EventMessage {
  Error: string
  Stacktrace: string | undefined
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private static _parkedMessage: EventMessage | undefined

  constructor(props: ErrorBoundaryProps) {
    super(props)

    this.state = { hasError: false }

    window.addEventListener('error', ErrorBoundary._errorEventHandler)
  }

  static geDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  private static _parkMessage(msg: EventMessage): void {
    ErrorBoundary._parkedMessage = msg
  }

  private static _unParkMessage(): void {
    ErrorBoundary._parkedMessage = undefined
  }

  private static _send(msg?: EventMessage): void {
    const url = location.href

    if (msg === undefined && ErrorBoundary._parkedMessage !== undefined) {
      process.env.NODE_ENV === 'production'
        ? void logAppEvent(AppEvent.UnhandledErrorOccured, {
            Error: ErrorBoundary._parkedMessage.Error,
            Stacktrace: ErrorBoundary._parkedMessage.Stacktrace,
            url,
          })
        : console.error('Event handler error', ErrorBoundary._parkedMessage)
    } else if (msg !== undefined) {
      process.env.NODE_ENV === 'production'
        ? void logAppEvent(AppEvent.UnhandledErrorOccured, {
            Error: msg.Error,
            Stacktrace: msg.Stacktrace,
            url,
          })
        : console.error('Component lifecycle error', msg)
    }

    ErrorBoundary._unParkMessage()
  }

  private static _errorEventHandler(event: ErrorEvent): void {
    ErrorBoundary._parkMessage({
      Error: `${(event.error as Error).name}: ${event.message}`,
      Stacktrace: (event.error as Error).stack,
    })
    setTimeout(() => ErrorBoundary._send(), 500)
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    ErrorBoundary._send({
      Error: `${error.name}: ${error.message}`,
      Stacktrace: errorInfo.componentStack,
    })
  }

  componentWillUnmount(): void {
    removeEventListener('error', ErrorBoundary._errorEventHandler)
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}
